import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { LEADERSHIP } from '../data/content';
import SEO from '../components/SEO';

const LeaderProfile = () => {
  const { id } = useParams();
  const leader = LEADERSHIP.find((l) => l.id === id);

  if (!leader) {
    return <Navigate to="/leadership" replace />;
  }

  const currentIndex = LEADERSHIP.findIndex((l) => l.id === id);
  const nextLeader = LEADERSHIP[(currentIndex + 1) % LEADERSHIP.length];

  return (
    <div className="bg-[#050505] text-white" data-testid="leader-profile-page">
      <SEO
        title={`${leader.name} — ${leader.title}`}
        description={leader.shortBio || `${leader.name}, ${leader.title} at Bronto Intelligence Private Limited.`}
        path={`/leadership/${leader.id}`}
        image={leader.photoUrl}
      />
      {/* Header / Hero */}
      <section className="relative pt-12 pb-20 px-6 lg:px-10 overflow-hidden">
        <div className="aurora-bg" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <Link
            to="/leadership"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-12 transition-colors"
            data-testid="back-to-leadership"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Leadership
          </Link>

          <div className="grid md:grid-cols-12 gap-12 items-start">
            {/* Photo */}
            <div className="md:col-span-5">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-gradient-to-br from-white/[0.06] to-white/[0.02] ring-1 ring-white/10">
                {leader.photoUrl ? (
                  <img
                    src={leader.photoUrl}
                    alt={leader.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-9xl font-bold text-white/15">
                      {leader.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="md:col-span-7 md:pt-8">
              <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
                {leader.title}
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8">
                {leader.name}
              </h1>
              <p className="text-lg md:text-xl text-white/70 leading-relaxed">
                {leader.shortBio}
              </p>

              {leader.responsibilities?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-8">
                  {leader.responsibilities.map((resp) => (
                    <span
                      key={resp}
                      className="text-xs font-medium text-white/70 px-3 py-1.5 rounded-full bg-white/5 border border-white/10"
                    >
                      {resp}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Full Biography */}
      <section className="py-20 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
            Biography
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-12">
            Full biography.
          </h2>

          <div className="space-y-6">
            {leader.fullBiography.map((para, idx) => (
              <p
                key={`${leader.id}-bio-${idx}`}
                className="text-base md:text-lg text-white/75 leading-relaxed"
                data-testid={`bio-paragraph-${idx}`}
              >
                {para}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Responsibilities Detail */}
      {leader.responsibilities?.length > 0 && (
        <section className="py-20 px-6 lg:px-10 border-t border-white/5">
          <div className="max-w-4xl mx-auto">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
              Areas of Responsibility
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-12">
              Strategic responsibilities.
            </h2>

            <div className="grid sm:grid-cols-2 gap-px bg-white/5 rounded-2xl overflow-hidden">
              {leader.responsibilities.map((resp, idx) => (
                <div key={idx} className="bg-[#050505] p-6 md:p-8">
                  <div className="text-xs font-semibold text-white/30 mb-2">0{idx + 1}</div>
                  <div className="text-lg font-semibold tracking-tight">{resp}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Next Profile */}
      <section className="py-20 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <Link
            to={`/leadership/${nextLeader.id}`}
            className="group block rounded-3xl glass hover-lift p-8 md:p-12"
            data-testid="next-leader-card"
          >
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-3">
              Next
            </div>
            <div className="flex items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-2">
                  {nextLeader.name}
                </h3>
                <p className="text-sm text-white/55">{nextLeader.title}</p>
              </div>
              <div className="w-14 h-14 rounded-full bg-white/10 group-hover:bg-white group-hover:text-black flex items-center justify-center transition-all shrink-0">
                <ArrowRight className="h-5 w-5" />
              </div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LeaderProfile;

